import { resolvePdfPageNumber } from './qualityReviewSource'

export const PDF_SOURCE_MAX_IMAGE_PIXELS = 16000000
export const PDF_SOURCE_MAX_CANVAS_BYTES = 64 * 1024 * 1024
export const PDF_SOURCE_MAX_RENDER_PIXELS = 8000000
export const PDF_SOURCE_MAX_RENDER_DIMENSION = 8192
export const PDF_SOURCE_MAX_TEXT_ITEMS = 20000
export const PDF_SOURCE_MAX_FILE_BYTES = 25 * 1024 * 1024
export const PDF_SOURCE_OPERATION_TIMEOUT_MS = 20000

export function createPdfPreviewError(code, message) {
    const error = new Error(message)
    error.code = code
    return error
}

export function buildSecurePdfDocumentOptions(data, pdfjs) {
    return {
        data,
        isEvalSupported: false,
        enableXfa: false,
        useWorkerFetch: false,
        useSystemFonts: false,
        disableFontFace: true,
        useWasm: false,
        isOffscreenCanvasSupported: false,
        isImageDecoderSupported: false,
        stopAtErrors: true,
        maxImageSize: PDF_SOURCE_MAX_IMAGE_PIXELS,
        canvasMaxAreaInBytes: PDF_SOURCE_MAX_CANVAS_BYTES,
        verbosity: pdfjs?.VerbosityLevel?.ERRORS ?? 0
    }
}

export function buildBoundedPdfViewport(page, { scale = 1.55, deviceScale = 1 } = {}) {
    const initial = page.getViewport({ scale })
    const safeDeviceScale = Math.max(1, Number(deviceScale || 1))
    const deviceWidth = initial.width * safeDeviceScale
    const deviceHeight = initial.height * safeDeviceScale
    const areaFactor = Math.sqrt(PDF_SOURCE_MAX_RENDER_PIXELS / Math.max(1, deviceWidth * deviceHeight))
    const widthFactor = PDF_SOURCE_MAX_RENDER_DIMENSION / Math.max(1, deviceWidth)
    const heightFactor = PDF_SOURCE_MAX_RENDER_DIMENSION / Math.max(1, deviceHeight)
    const factor = Math.min(1, areaFactor, widthFactor, heightFactor)
    return page.getViewport({ scale: scale * factor })
}

export function withPdfOperationTimeout(promise, onTimeout, timeoutMs = PDF_SOURCE_OPERATION_TIMEOUT_MS) {
    let timeoutId
    const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            onTimeout?.()
            reject(createPdfPreviewError(
                'PDF_PREVIEW_TIMEOUT',
                'The focused PDF preview took too long and was stopped safely.'
            ))
        }, timeoutMs)
    })
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
}

export function hasPdfHeader(data) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data || 0)
    const prefix = bytes.subarray(0, Math.min(bytes.length, 1024))
    const text = Array.from(prefix, byte => String.fromCharCode(byte)).join('')
    return text.includes('%PDF-')
}

export async function readBoundedPdfTextContent(page, maxItems = PDF_SOURCE_MAX_TEXT_ITEMS) {
    const reader = page.streamTextContent().getReader()
    const items = []
    let lang = null

    try {
        while (true) {
            const { value, done } = await reader.read()
            if (done) return { items, lang, tooMany: false }
            lang ||= value?.lang || null
            items.push(...(value?.items || []))
            if (items.length > maxItems) {
                await reader.cancel('Focused evidence matching limit reached.')
                return { items: [], lang, tooMany: true }
            }
        }
    } finally {
        reader.releaseLock?.()
    }
}

export function safePdfPreviewMessage(error) {
    const messages = {
        PDF_PREVIEW_TIMEOUT: 'The focused PDF preview took too long and was stopped safely.',
        PDF_WORKER_UNAVAILABLE: 'The secure PDF worker is unavailable, so this file was not opened.',
        PDF_FILE_TOO_LARGE: 'This PDF exceeds the 25 MB focused-preview limit.',
        PDF_INVALID_FILE: 'This file does not have a valid PDF header, so it was not opened.',
        PDF_CANVAS_UNAVAILABLE: 'The secure PDF preview could not create a drawing surface.'
    }
    return messages[error?.code] || 'This PDF page could not be displayed safely in the focused preview.'
}

export function validatePdfPageReference(source, totalPages) {
    const requestedPage = resolvePdfPageNumber(source)
    const availablePages = Number(totalPages || 0)
    if (!Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage > availablePages) {
        return {
            valid: false,
            requestedPage,
            totalPages: availablePages,
            message: `The saved reference points to PDF page ${requestedPage}, but this file has ${availablePages} page${availablePages === 1 ? '' : 's'}. No different page was opened.`
        }
    }
    return { valid: true, requestedPage, totalPages: availablePages, message: '' }
}
